import {
    IGeoComplyResponse,
    IGeoLocationStrategy,
    IGeoLocationStrategyRunParams
} from "../geo-location.strategy.interface";
import {IServiceFactoryExtended} from "../../../service-factory-extended.interface";
import {GeoComplyErrorCodes, GeoComplyFailureReason} from "../geo-comply-error-codes";
import {Check} from "@sparkware/app-utils";
import {GeoAnalyticsEvent} from "../../geo-location-analytics";

interface IHybridGeoResult {
    type: string;
    data: string;
    code: string;
}

export class GeoLocationHybridStrategy implements IGeoLocationStrategy {
    constructor(private readonly services: IServiceFactoryExtended) {
        if(!GeoLocationHybridStrategy._hybridGeoLocationEnabled) {
            services.sdk.eventsTrigger.enableHybridGeoLocation(true);
            GeoLocationHybridStrategy._hybridGeoLocationEnabled = true;
        }

    }

    private static _hybridGeoLocationEnabled = false;

    dispose(): void {
    }

    private _parseHybridGeoResult(hybridGeoResult: string): IHybridGeoResult | null {

        try {
            return JSON.parse(hybridGeoResult)
        } catch {
            return null;
        }
    }

    private _convertHybridResultToGeoComplyResponse(hybridGeoResult: IHybridGeoResult): IGeoComplyResponse {
        if(hybridGeoResult.type === '1') { //success
            return {
                data: hybridGeoResult.data
            };
        }

        if(hybridGeoResult.type === '0') { //fail
            const errorCode = parseInt(hybridGeoResult.code);

            if(Check.isNumber(errorCode)) {
                return {
                    failure: {
                        geoComplyError: {
                            errorCode: errorCode,
                            errorMessage: hybridGeoResult.data
                        },
                        additionalFailureReason: GeoComplyFailureReason.TechnicalProblem
                    }
                };
            } else {
                return {
                    failure: {
                        geoComplyError: {
                          errorCode: GeoComplyErrorCodes.UnknownError,
                          errorMessage: hybridGeoResult.data
                        },
                        additionalFailureReason: GeoComplyFailureReason.TechnicalProblem
                    }
                };
            }
        }

        return {
            failure: {
                additionalFailureReason: GeoComplyFailureReason.TechnicalProblem
            }
        };
    }

    runGeoLocation(params: IGeoLocationStrategyRunParams): Promise<IGeoComplyResponse> {
        return new Promise<IGeoComplyResponse>(resolve => {
            let timeoutRef: any;
            const hybridResponseSubscription = this.services.sdk.events.onHybridGeoLocationResponse(hybridGeoPayload => {
                hybridResponseSubscription.off();
                clearTimeout(timeoutRef);

                const hybridGeoResult = this._parseHybridGeoResult(hybridGeoPayload);
                if(hybridGeoResult) {
                    resolve(this._convertHybridResultToGeoComplyResponse(hybridGeoResult));
                } else {
                    params.analytics.reportEvent({
                        geoEventName: GeoAnalyticsEvent.ParseHybridGeoResponseFailure,
                        failureReason: GeoComplyFailureReason.TechnicalProblem,
                        errorMessage: hybridGeoPayload
                    })
                    resolve({
                        failure: {
                            additionalFailureReason: GeoComplyFailureReason.TechnicalProblem
                        }
                    });
                }
            });

            timeoutRef = setTimeout(() => {
                hybridResponseSubscription.off();
                params.analytics.reportEvent({
                    geoEventName: GeoAnalyticsEvent.HybridGeoTimeout
                });

                resolve({
                    failure: {
                        additionalFailureReason: GeoComplyFailureReason.GeoComplyTimeout
                    }
                });
            }, this.services.configuration.servicesConfigs.GeoLocation.geoProcessTimeout);

            this.services.sdk.eventsTrigger.hybridGeoLocation({
                sessionKey: params.locationTransactionId,
                license: params.licenseKey,
                userId: this.services.user.cid.toString(),
                phone: params.mobileNumber,
                reason: params.reason
            });
        });
    }

}